/**
 * @architecture-header 1
 * @layer routes
 * @domain settings
 * @responsibility Render the teacher-only Settings navigation shell, delegating each tab's pane to a
 *   lane-owned content seam and falling back to a placeholder for tabs no card has claimed yet.
 * @state-owner URL route owns the selected tab; parent owns navigation and loading state; each pane
 *   seam owns its own server state
 * @allowed-imports React; shared workspace loading/translator state; lane-owned pane content seams
 * @forbidden direct API calls or mutations in this file; student settings
 * @public-api WorkspaceShell
 * @side-effects invokes route navigation callbacks
 * @tests SettingsShell.test.tsx
 */

import { agentASettingsPaneContent } from '../../app/feature-composition/agent-a/SettingsPaneContent';
import { VispoLoading } from '../../shared/components/WorkspaceState';
import { useTranslator } from '../../shared/model/use-workspace';

export type SettingsTab =
  | 'account'
  | 'preferences'
  | 'general'
  | 'users'
  | 'invitations'
  | 'ai-assistant'
  | 'sso-security'
  | 'subscription';

type SettingsTabDefinition = { label: string; value: SettingsTab };
type SettingsTabGroup = { label: string; tabs: readonly SettingsTabDefinition[] };

export const SETTINGS_TAB_GROUPS = [
  {
    label: 'Personal',
    tabs: [
      { label: 'Account', value: 'account' },
      { label: 'Preferences', value: 'preferences' },
    ],
  },
  {
    label: 'Organization',
    tabs: [
      { label: 'General', value: 'general' },
      { label: 'Users', value: 'users' },
      { label: 'Invitations', value: 'invitations' },
      { label: 'AI Assistant', value: 'ai-assistant' },
      { label: 'SSO & Security', value: 'sso-security' },
      { label: 'Subscription', value: 'subscription' },
    ],
  },
] as const satisfies readonly SettingsTabGroup[];

const SETTINGS_TABS: readonly SettingsTabDefinition[] = [
  ...SETTINGS_TAB_GROUPS[0].tabs,
  ...SETTINGS_TAB_GROUPS[1].tabs,
];

export function normalizeSettingsTab(segment?: string): SettingsTab {
  return SETTINGS_TABS.find((tab) => tab.value === segment)?.value ?? 'account';
}

function tabLabel(tab: SettingsTab): string {
  return SETTINGS_TABS.find((item) => item.value === tab)?.label ?? 'Account';
}

function settingsTabLabel(t: ReturnType<typeof useTranslator>, tab: SettingsTab): string {
  if (tab === 'account') return t('帳號');
  if (tab === 'preferences') return t('偏好設定');
  return tabLabel(tab);
}

function SettingsNavigation({
  onSelect,
  tab,
}: {
  onSelect(tab: SettingsTab): void;
  tab: SettingsTab;
}) {
  const t = useTranslator();
  return (
    <nav aria-label="Settings navigation" className="settings-shell__navigation">
      {SETTINGS_TAB_GROUPS.map((group) => (
        <section className="settings-shell__group" key={group.label}>
          <h2>{group.label}</h2>
          <div className="settings-shell__tabs">
            {group.tabs.map((item) => (
              <button
                aria-current={item.value === tab ? 'page' : undefined}
                className={item.value === tab ? 'is-selected' : ''}
                key={item.value}
                type="button"
                onClick={() => onSelect(item.value)}
              >
                {settingsTabLabel(t, item.value)}
              </button>
            ))}
          </div>
        </section>
      ))}
    </nav>
  );
}

function SettingsPane({
  loading,
  surface,
  tab,
}: {
  loading: boolean;
  surface: 'desktop' | 'mobile';
  tab: SettingsTab;
}) {
  const t = useTranslator();
  const label = settingsTabLabel(t, tab);
  const fallback = (
    <section aria-label={`${label} settings`} className="settings-shell__pane">
      {loading ? (
        <VispoLoading className="settings-shell__loading" label={`Loading ${label} settings`} />
      ) : (
        <div className="settings-shell__placeholder">
          <span className="settings-shell__eyebrow">{label}</span>
          <h2>{label} is being prepared.</h2>
          <p>This space will hold its own settings when it is ready.</p>
        </div>
      )}
    </section>
  );
  if (loading) return fallback;
  const agentA = agentASettingsPaneContent({ tab, surface }, fallback);
  return agentA ?? fallback;
}

export function SettingsShell({
  loading,
  onBack,
  onSelect,
  surface,
  tab,
}: {
  loading: boolean;
  onBack?: () => void;
  onSelect(tab: SettingsTab): void;
  surface: 'desktop' | 'mobile';
  tab: SettingsTab;
}) {
  if (surface === 'mobile') {
    return (
      <section aria-label="Settings" className="settings-shell settings-shell--mobile">
        <header className="settings-shell__mobile-header">
          <button aria-label="Back" type="button" onClick={onBack}>
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h1>Settings</h1>
          <span aria-hidden="true" />
        </header>
        <div className="settings-shell__mobile-body">
          <SettingsNavigation onSelect={onSelect} tab={tab} />
          <SettingsPane loading={loading} surface="mobile" tab={tab} />
        </div>
      </section>
    );
  }

  return (
    <main aria-label="Settings" className="settings-shell settings-shell--desktop">
      <h1 className="sr-only">Settings</h1>
      <SettingsNavigation onSelect={onSelect} tab={tab} />
      <SettingsPane loading={loading} surface="desktop" tab={tab} />
    </main>
  );
}
